import { Component, computed, ElementRef, forwardRef, inject, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Perfil } from '../../core/models/perfil.model';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { normalizarTexto } from '../../core/utils/texto.util';

interface OpcaoPerfil {
  id: number;
  nome: string;
  data: string;
  rotulo: string;
}

/** Select com busca de perfis; o valor do controle é o `id` do perfil escolhido. */
@Component({
  selector: 'app-perfil-autocomplete',
  standalone: true,
  templateUrl: './perfil-autocomplete.component.html',
  styleUrl: './perfil-autocomplete.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PerfilAutocompleteComponent),
      multi: true,
    },
  ],
})
export class PerfilAutocompleteComponent implements ControlValueAccessor {
  readonly perfis = input.required<Perfil[]>();
  readonly inputId = input.required<string>();

  readonly texto = signal('');
  readonly aberto = signal(false);
  readonly indiceAtivo = signal(-1);
  readonly desabilitado = signal(false);

  readonly listaId = computed(() => `${this.inputId()}-lista`);

  readonly opcoes = computed<OpcaoPerfil[]>(() =>
    this.perfis().map((perfil) => {
      const data = formatDateIsoToBr(perfil.ultimaAtualizacao);
      return { id: perfil.id, nome: perfil.nome, data, rotulo: `${perfil.nome} - ${data}` };
    }),
  );

  readonly opcoesFiltradas = computed(() => {
    const termo = normalizarTexto(this.texto());
    if (!termo) {
      return this.opcoes();
    }
    return this.opcoes().filter((opcao) => normalizarTexto(opcao.rotulo).includes(termo));
  });

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private idSelecionado: number | null = null;
  private onChange: (id: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(id: number | null): void {
    this.idSelecionado = id;
    this.texto.set(this.opcoes().find((opcao) => opcao.id === id)?.rotulo ?? '');
  }

  registerOnChange(fn: (id: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.desabilitado.set(desabilitado);
    this.fechar();
  }

  onDigitar(valor: string): void {
    this.texto.set(valor);
    this.abrir();
    // Texto editado deixa de corresponder ao perfil escolhido: o "+" só volta a valer
    // quando o usuário selecionar uma opção da lista.
    this.atualizarSelecao(null);
  }

  onTeclar(evento: KeyboardEvent): void {
    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault();
        this.moverIndiceAtivo(1);
        break;
      case 'ArrowUp':
        evento.preventDefault();
        this.moverIndiceAtivo(-1);
        break;
      case 'Enter':
        if (this.aberto() && this.indiceAtivo() >= 0) {
          evento.preventDefault();
          this.selecionar(this.opcoesFiltradas()[this.indiceAtivo()]);
        }
        break;
      case 'Escape':
        this.fechar();
        break;
    }
  }

  onSairDoCampo(): void {
    this.fechar();
    this.onTouched();
  }

  alternarLista(): void {
    if (this.aberto()) {
      this.fechar();
      return;
    }
    this.abrir();
  }

  selecionar(opcao: OpcaoPerfil): void {
    this.texto.set(opcao.rotulo);
    this.atualizarSelecao(opcao.id);
    this.fechar();
  }

  opcaoId(indice: number): string {
    return `${this.listaId()}-opcao-${indice}`;
  }

  estaSelecionada(opcao: OpcaoPerfil): boolean {
    return opcao.id === this.idSelecionado;
  }

  private abrir(): void {
    if (this.desabilitado()) {
      return;
    }
    this.aberto.set(true);
    this.indiceAtivo.set(-1);
  }

  private fechar(): void {
    this.aberto.set(false);
    this.indiceAtivo.set(-1);
  }

  private moverIndiceAtivo(passo: number): void {
    if (!this.aberto()) {
      this.abrir();
    }
    const total = this.opcoesFiltradas().length;
    if (total === 0) {
      return;
    }
    // Sem item ativo, a seta para cima começa do último, como num select nativo.
    const atual = this.indiceAtivo() < 0 && passo < 0 ? 0 : this.indiceAtivo();
    const proximo = (atual + passo + total) % total;
    this.indiceAtivo.set(proximo);
    this.host.nativeElement
      .querySelector(`#${this.opcaoId(proximo)}`)
      ?.scrollIntoView({ block: 'nearest' });
  }

  private atualizarSelecao(id: number | null): void {
    if (id === this.idSelecionado) {
      return;
    }
    this.idSelecionado = id;
    this.onChange(id);
  }
}
